/**
 * Недавні записи людини (UX-04; shell-navigation.md; autonumber-tags.md, розд. 3): картки, які вона
 * відкривала, — для порожнього запиту палітри `Ctrl+K`. Живуть на сервері, за людиною
 * (`/api/v1/me/recent`), однаково на всіх її пристроях; назву сервер дає на читанні з її правами.
 *
 * У браузері недавніх немає: ні назв, ні id записів (threat-model.md: «дані в localStorage на
 * спільному ПК»). Лишається лише мітка власника — id людини з `/me`, що відкривала картки на цьому
 * пристрої (`storedRecentOwner`, для мітки `next`: app/next-owner.ts), як і досі: мітка з'являється
 * з першою відкритою карткою, інший власник її стирає, вихід — теж. Старий формат (`v: 1`, з id
 * записів) переписується без них при першому ж читанні мітки.
 *
 * До відповіді `/me` відкриті картки чекають у пам'яті вкладки і йдуть на сервер, щойно власник
 * відомий. Відмітка — `PUT` з `keepalive`: доходить, навіть якщо людина одразу пішла зі сторінки.
 */

import type { RecentRecords } from '@clientline/contracts';
import { api } from './api.ts';

const KEY = 'cl:recent-records';
/** Скільки карток чекають власника в пам'яті вкладки (стільки ж пам'ятає сервер). */
const PENDING_LIMIT = 50;

interface Stored {
  v: 2;
  owner: string;
}

interface Opened {
  entity: string;
  id: string;
}

let owner: string | null = null;
let pending: Opened[] = [];
/** Відмітки в дорозі: читання недавніх чекає їх, щоб щойно відкрита картка вже була в списку. */
const inflight = new Set<Promise<void>>();

/**
 * Власник з мітки цього пристрою. Старий формат (`v: 1`, з id записів) переписується одразу, при
 * першому ж читанні, а не після `/me`: на пристрої, куди людина більше не входить, id не лишаються.
 */
function readOwner(): string | null {
  let raw: { v?: unknown; owner?: unknown } | null;
  try {
    raw = JSON.parse(localStorage.getItem(KEY) ?? 'null') as typeof raw;
  } catch {
    return null;
  }
  if ((raw?.v !== 1 && raw?.v !== 2) || typeof raw.owner !== 'string') return null;
  if (raw.v === 1) writeOwner(raw.owner);
  return raw.owner;
}

function writeOwner(id: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ v: 2, owner: id } satisfies Stored));
  } catch {
    // Приватний режим чи повне сховище: мітки немає, `next` лишається без неї, як посилання.
  }
}

const path = (entity: string, id: string) =>
  `/api/v1/me/recent/${encodeURIComponent(entity)}/${encodeURIComponent(id)}`;

function send(opened: Opened): void {
  // Збій відмітки — лише картка не потрапить у недавні; повтору не треба: наступне відкриття відмітить.
  const done = api<void>('PUT', path(opened.entity, opened.id), { keepalive: true }).then(
    () => undefined,
    () => undefined,
  );
  inflight.add(done);
  void done.finally(() => inflight.delete(done));
}

function removeOwner(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Сховище недоступне — нічого й не збережено.
  }
}

/** Хто увійшов (id з `/me`): мітка власника пристрою, картки, що чекали, — на сервер. */
export function setRecentOwner(id: string): void {
  owner = id;
  const stored = readOwner();
  // Мітка іншої людини — геть; своя (зокрема старий формат з id записів) — переписати без них.
  if (stored !== null && stored !== id) removeOwner();
  if (stored === id || pending.length > 0) writeOwner(id);
  const queued = pending;
  pending = [];
  // Найстаріша — першою: на сервері остання відмітка стане найновішою.
  for (const opened of [...queued].reverse()) send(opened);
}

/** Картку відкрито (а не лише переглянуто рядок чи підвантажено заздалегідь). */
export function rememberRecord(entity: string, id: string): void {
  if (owner) {
    if (readOwner() !== owner) writeOwner(owner);
    send({ entity, id });
    return;
  }
  pending = [{ entity, id }, ...pending.filter((x) => !(x.entity === entity && x.id === id))].slice(
    0,
    PENDING_LIMIT,
  );
}

/**
 * Чиї недавні на цьому пристрої: id людини, що входила останньою і не виходила (вихід мітку
 * стирає), або `null`. Для мітки `next` (app/next-owner.ts), коли `/me` ще немає.
 */
export function storedRecentOwner(): string | null {
  return readOwner();
}

/**
 * Недавні людини з сервера, від останнього: лише ті, що вона бачить зараз, з назвою за її правами
 * (`label: null` — назва прихована чи порожня). Спершу чекає відміток у дорозі.
 */
export async function fetchRecentRecords(
  limit: number,
  signal?: AbortSignal,
): Promise<RecentRecords['items']> {
  if (!owner) return [];
  await Promise.all([...inflight]);
  const page = await api<RecentRecords>('GET', `/api/v1/me/recent?limit=${limit}`, {
    ...(signal ? { signal } : {}),
  });
  return page.items;
}

/** «Очистити недавні» з меню користувача (спільний ПК): на сервері, для всіх пристроїв людини. */
export async function clearRecentRecords(): Promise<void> {
  pending = [];
  await Promise.all([...inflight]);
  await api<void>('DELETE', '/api/v1/me/recent');
}

/**
 * Вихід: прибрати все недавнє цієї вкладки і пристрою — чергу й мітку власника. Сервер не чіпаємо:
 * сесії вже може не бути, а недавні людини, крім неї, ніхто не читає.
 */
export function forgetRecentOnDevice(): void {
  owner = null;
  pending = [];
  removeOwner();
}

/** Лише для тестів: забути власника, чергу й відмітки в дорозі. */
export function resetRecentForTests(): void {
  owner = null;
  pending = [];
  inflight.clear();
}
